import React, { useEffect, useRef, useState } from 'react';
import * as L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './Map.scss';
import { MapPin, Clock, Briefcase } from 'lucide-react';

const DEFAULT_LOCATION = {
  lat: 8.181218,
  lng: 77.438086
};

const Map: React.FC = () => {
  const mapRef = useRef<HTMLDivElement>(null);
  const mapInstance = useRef<L.Map | null>(null);
  const markerRef = useRef<L.Marker | null>(null);
  const [isSatellite, setIsSatellite] = useState(false);
  const [address, setAddress] = useState('Loading location...');

  const GEOAPIFY_API_KEY = '58269ca295f241d4a1522540efbf3c77';

  useEffect(() => {
    if (mapRef.current && !mapInstance.current) {
      mapInstance.current = L.map(mapRef.current).setView(
        [DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng],
        13
      );

      const baseLayer = L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
        attribution: '&copy; OpenStreetMap contributors',
        maxZoom: 19
      });

      const satelliteLayer = L.tileLayer(
        'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}',
        {
          attribution: '&copy; Esri, Earthstar Geographics',
          maxZoom: 19
        }
      );

      baseLayer.addTo(mapInstance.current);

      const redPinIcon = L.divIcon({
        html: `
          <svg xmlns="http://www.w3.org/2000/svg" width="36" height="36" viewBox="0 0 24 24" fill="rgba(255, 39, 64, 0.82)">
            <path d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7zm0 9.5a2.5 2.5 0 1 1 0-5 2.5 2.5 0 0 1 0 5z"/>
          </svg>
        `,
        className: 'custom-marker-icon',
        iconSize: [36, 36],
        iconAnchor: [18, 36]
      });

      markerRef.current = L.marker([DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng], {
        icon: redPinIcon
      }).addTo(mapInstance.current);

      const fetchAddress = async (lat: number, lng: number) => {
        try {
          const response = await fetch(
            `https://api.geoapify.com/v1/geocode/reverse?lat=${lat}&lon=${lng}&apiKey=${GEOAPIFY_API_KEY}`
          );
          const data = await response.json();
          if (data.features?.length > 0) {
            const formatted = data.features[0].properties.formatted;
            setAddress(formatted);
            markerRef.current?.bindPopup(`<strong>${formatted}</strong>`).openPopup();
          } else {
            setAddress('Address not found');
          }
        } catch (err) {
          console.error('Error:', err);
          setAddress('Error fetching address');
        }
      };

      fetchAddress(DEFAULT_LOCATION.lat, DEFAULT_LOCATION.lng);

      const toggleLayer = () => {
        if (!mapInstance.current) return;
        if (isSatellite) {
          satelliteLayer.remove();
          baseLayer.addTo(mapInstance.current);
        } else {
          baseLayer.remove();
          satelliteLayer.addTo(mapInstance.current);
        }
      };

      toggleLayer();

      return () => {
        mapInstance.current?.remove();
        mapInstance.current = null;
      };
    }
  }, [isSatellite]);

  return (
    <section className="map" id="location">
      <div className="map__container">
        <h2 className="map__title">Location</h2>

        <div
          ref={mapRef}
          className="map__wrapper"
          style={{ height: '400px', borderRadius: '12px', overflow: 'hidden' }}
        />

        <button
          onClick={() => setIsSatellite(!isSatellite)}
          className="map__toggle"
          style={{
            background: '#ff2740',
            color: '#fff',
            padding: '8px 16px',
            borderRadius: '8px',
            marginTop: '1rem',
            border: 'none',
            cursor: 'pointer'
          }}
        >
          Switch to {isSatellite ? 'Street View' : 'Satellite View'}
        </button>

        <div className="map__info" style={{ marginTop: '1.5rem', lineHeight: 1.7 }}>
          <div className="map__info-item" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <MapPin size={18} strokeWidth={2} /> <span>{address}</span>
          </div>
          <div className="map__info-item" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Clock size={18} strokeWidth={2} /> <span>Time Zone: IST (UTC+5:30)</span>
          </div>
          <div className="map__info-item" style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
            <Briefcase size={18} strokeWidth={2} /> <span>Open to: Remote • Relocation • Hybrid</span>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Map;
